/**
 * useCourses Hook
 * React hook for fetching and managing course data with auto-refresh
 */

import { useState, useEffect, useCallback } from 'react';
import { getCourses, subscribeToCourses } from '@/services/courseService';
import type { Course } from '@/types/course';

interface UseCoursesOptions {
  autoRefresh?: boolean;
  refreshInterval?: number; // in milliseconds
  enableRealtime?: boolean;
}

interface UseCoursesReturn {
  courses: Course[];
  loading: boolean;
  error: Error | null;
  refetch: () => Promise<void>;
}

/**
 * Hook to fetch and manage courses
 * @param options Configuration options
 * @returns Course data, loading state, error, and refetch function
 */
export const useCourses = (options: UseCoursesOptions = {}): UseCoursesReturn => {
  const {
    autoRefresh = false,
    refreshInterval = 60000, // 1 minute default
    enableRealtime = true,
  } = options;

  const [courses, setCourses] = useState<Course[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  // Memoize fetchCourses to avoid unnecessary recreations
  const fetchCourses = useCallback(async () => {
    try {
      setLoading(true);
      const { data, error: fetchError } = await getCourses();
      
      // Always use the data (either from API or fallback)
      if (data && data.length > 0) {
        setCourses(data);
        // Only set error if we got an error but still have data (using fallback)
        if (fetchError) {
          setError(fetchError);
        } else {
          setError(null);
        }
      } else if (fetchError) {
        // Only set error if we have no data at all
        setError(fetchError);
      }
    } catch (err) {
      setError(err as Error);
      // Even on catch, try to ensure we have fallback data
      const { data } = await getCourses();
      if (data && data.length > 0) {
        setCourses(data);
      }
    } finally {
      setLoading(false);
    }
  }, []);

  // Initial fetch
  useEffect(() => {
    fetchCourses();
  }, [fetchCourses]);

  // Auto-refresh polling - optimized with dependency check
  useEffect(() => {
    if (!autoRefresh) return;

    const interval = setInterval(() => {
      fetchCourses();
    }, refreshInterval);

    return () => clearInterval(interval);
  }, [autoRefresh, refreshInterval, fetchCourses]);

  // Real-time subscription
  useEffect(() => {
    if (!enableRealtime) return;

    const subscription = subscribeToCourses((updatedCourses) => {
      setCourses(updatedCourses);
    });

    return () => {
      if (subscription) {
        subscription.unsubscribe();
      }
    };
  }, [enableRealtime]);

  return {
    courses,
    loading,
    error,
    refetch: fetchCourses,
  };
};


